import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Typography,
    Divider,
    TextField,
    Menu,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Autocomplete,
    Chip,
    InputAdornment,
    CircularProgress,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import DataBITable from "./DataBITable"; // Changed import
import { useAuth } from "../../../helpers/AuthProvider";
import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";

// --- Interface Definitions ---
interface DataBIParticipantInfo {
    id: string;
    name: string;
    googleProfilePicture?: string;
}

interface DataBIApplicationInfo {
    id: string;
    applicationName: string;
}

interface DataBISpecificDetails {
    region?: string;
    edition?: string;
    primaryDataSource?: string;
}

interface DataBIJob {
    id: string;
    name: string;
    application?: DataBIApplicationInfo;
    cloudProvider?: string;
    solutionCategoryType?: string;
    costLabelPairs?: { cost: string; label: string }[];
    status: 'pending' | 'active' | 'rejected' | string;
    details?: {
        dataAnalytics?: DataBISpecificDetails;
    };
    reasonForRejection?: string;
    createdAt?: string;
    updatedAt?: string;
    solutionOwner?: DataBIParticipantInfo | null;
}

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof DataBIJob | "solutionOwner.name" | "details.dataAnalytics.region" | "application.applicationName" | string;
    groupBy: string;
}

const DataBI: React.FC = () => {
    const [allDataBIServices, setAllDataBIServices] = useState<DataBIJob[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [searchOptionsMasterList, setSearchOptionsMasterList] = useState<SearchOptions[]>([]);

    const initialColumnFilters: Record<string, boolean> = {
        name: true,
        application: true,
        cloudProvider: true,
        analyticsServiceType: true,
        region: true,
        solutionOwner: true,
        createdAt: true,
    };
    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
    const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await queryGraphql(`
                    query GetDataAnalytics {
                      getDataAnalytics {
                        id
                        name
                        application {
                           id
                           applicationName
                        }
                        cloudProvider
                        solutionCategoryType
                        status
                        details {
                          dataAnalytics {
                            region
                          }
                        }
                        createdAt
                        solutionOwner {
                          id
                          name
                          googleProfilePicture
                        }
                      }
                    }
                `);

                const dataAnalyticsData = response.getDataAnalytics;
                if (dataAnalyticsData) {
                    const processedServices = dataAnalyticsData.map((da: any): DataBIJob => ({
                        id: da.id,
                        name: da.name || "Unnamed Service",
                        application: da.application ? {
                            id: da.application.id,
                            applicationName: da.application.applicationName,
                        } : undefined,
                        cloudProvider: da.cloudProvider,
                        solutionCategoryType: da.solutionCategoryType,
                        status: (da.status || 'pending').toLowerCase(),
                        details: da.details,
                        createdAt: da.createdAt,
                        solutionOwner: da.solutionOwner ? {
                            id: da.solutionOwner.id,
                            name: da.solutionOwner.name,
                            googleProfilePicture: da.solutionOwner.googleProfilePicture,
                        } : null,
                    }));
                    setAllDataBIServices(processedServices);

                    let dynamicSearchOptions: SearchOptions[] = processedServices
                        .map((service: DataBIJob) => [
                            { category: "name", groupBy: "SERVICE NAME", chipLabel: `Name: ${service.name}`, dropdownLabel: service.name },
                            ...(service.application ? [{ category: "application.applicationName" as any, groupBy: "APPLICATION", chipLabel: `App: ${service.application.applicationName}`, dropdownLabel: service.application.applicationName }] : []),
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${service.status}`, dropdownLabel: service.status.charAt(0).toUpperCase() + service.status.slice(1) },
                            ...(service.cloudProvider ? [{ category: "cloudProvider" as any, groupBy: "CLOUD PROVIDER", chipLabel: `Cloud: ${service.cloudProvider}`, dropdownLabel: service.cloudProvider }] : []),
                            ...(service.solutionCategoryType ? [{ category: "solutionCategoryType" as any, groupBy: "SERVICE TYPE", chipLabel: `Type: ${service.solutionCategoryType}`, dropdownLabel: service.solutionCategoryType }] : []),
                            ...(service.details?.dataAnalytics?.region ? [{ category: "details.dataAnalytics.region" as any, groupBy: "REGION", chipLabel: `Region: ${service.details.dataAnalytics.region}`, dropdownLabel: service.details.dataAnalytics.region }] : []),
                            ...(service.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "OWNER", chipLabel: `Owner: ${service.solutionOwner.name}`, dropdownLabel: service.solutionOwner.name }] : []),
                        ])
                        .flat();

                    dynamicSearchOptions = Array.from(new Map(dynamicSearchOptions.map(item => [JSON.stringify(item), item])).values());
                    
                    dynamicSearchOptions.sort((a, b) => {
                        const groupA = a.groupBy.toUpperCase();
                        const groupB = b.groupBy.toUpperCase();
                        if (groupA > groupB) return 1;
                        if (groupA < groupB) return -1;
                        const labelA = String(a.dropdownLabel).toUpperCase();
                        const labelB = String(b.dropdownLabel).toUpperCase();
                        if (labelA > labelB) return 1;
                        if (labelA < labelB) return -1;
                        return 0;
                    });
                    setSearchOptionsMasterList(dynamicSearchOptions);
                }
            } catch (error) {
                console.error("Error fetching Data Analytics & BI Tools:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempColumnFilters(columnFilters);
        setColumnFilterAnchorEl(event.currentTarget);
    };

    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const handleTempColumnFilterChange = (column: string) => {
        setTempColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    const handleApplyColumnFilters = () => {
        setColumnFilters(tempColumnFilters);
        handleColumnFilterClose();
    };

    const getDisplayLabelForKey = (key: string): string => {
        const labels: Record<string, string> = {
            name: 'Service Name',
            application: 'Application',
            cloudProvider: 'Cloud Provider',
            analyticsServiceType: 'Service Type',
            region: 'Region',
            solutionOwner: 'Solution Owner',
            createdAt: 'Added On'
        };
        return labels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    };


    const globallyFilteredServices =
        searchQuery.length === 0
            ? allDataBIServices
            : allDataBIServices.filter((service: DataBIJob) => {
                return searchQuery.every(queryParam => {
                    let serviceValue: any;
                    if (queryParam.category === "application.applicationName") {
                        serviceValue = service.application?.applicationName;
                    } else if (queryParam.category === "solutionOwner.name") {
                        serviceValue = service.solutionOwner?.name;
                    } else if (queryParam.category === "details.dataAnalytics.region") {
                        serviceValue = service.details?.dataAnalytics?.region;
                    } else {
                        serviceValue = service[queryParam.category as keyof DataBIJob];
                    }

                    if (serviceValue === null || serviceValue === undefined) return false;
                    return String(serviceValue).toLowerCase().includes(String(queryParam.dropdownLabel).toLowerCase());
                });
            });

    const pendingServices = globallyFilteredServices.filter(job => job.status === 'pending' || job.status === 'submitted' || job.status === 'provisioning');
    const activeServices = globallyFilteredServices.filter(job => job.status === 'active' || job.status === 'approved' || job.status === 'success');
    const rejectedServices = globallyFilteredServices.filter(job => job.status === 'rejected' || job.status === 'failed');

    const totalServicesOverall = allDataBIServices.length;
    const totalMatchingSearch = globallyFilteredServices.length;

    const canAddDataBI = user.roles.includes("cluster_owner") || user.roles.includes("viewer");

    const handleAddDataBI = () => {
        navigate("/datasolutions/data-bi/add");
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex" }}>
                <MainDrawer index={"datasolutions-data-bi"} />
                <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"datasolutions-data-bi"} />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <MiniBot />
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={{ xs: 1.5, sm: 2 }} mb="18px" >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: "space-between",
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                mx: 2
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
                                Data Analytics & BI
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {canAddDataBI && (
                                    <Button
                                        variant="contained"
                                        sx={{
                                            backgroundColor: "black", color: "white", padding: "8px 24px",
                                            fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
                                            borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
                                        }}
                                        onClick={handleAddDataBI}
                                        disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
                                    >
                                        Add New BI Tool
                                    </Button>
                                )}
                                <IconButton
                                    aria-label="Help"
                                    onClick={() => window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs`, "_blank")}
                                    sx={{
                                        padding: 0,
                                        border: 'none',
                                        ml: 2,
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src="/assets/Help.svg"
                                        alt="Help"
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            display: 'block',
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
                            <Autocomplete
                                multiple
                                options={searchOptionsMasterList}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal as SearchOptions[]);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by name, type, status, etc."
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={3} {...props} />}
                            />
                            <Button
                                                            sx={{
                                                                border: "1px solid #bbb",
                                                                borderRadius: "10px",
                                                                height: "45px",
                                                                width: "45px",
                                                            }}
                                                            onClick={handleColumnFilterClick}
                                                        >
                                                            <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                                                        </Button>
                            <Menu
                                anchorEl={columnFilterAnchorEl}
                                open={Boolean(columnFilterAnchorEl)}
                                onClose={handleColumnFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
                            >
                                <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.entries(tempColumnFilters).map(([key, value]) => (
                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Checkbox
                                                        checked={value}
                                                        onChange={() => handleTempColumnFilterChange(key)}
                                                        size="small"
                                                        sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
                                                    />
                                                }
                                                label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
                                                sx={{ marginBottom: '4px' }}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button
                                        variant="contained" fullWidth onClick={handleApplyColumnFilters}
                                        sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>

                    <Box m={{ xs: 1, sm: 2 }}>
                        {totalServicesOverall > 0 && totalMatchingSearch > 0 && (
                            <>
                                <Accordion defaultExpanded={pendingServices.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={pendingServices.length === 0}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/clock.svg" alt="Pending" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Pending ({pendingServices.length})</Typography></Box></AccordionSummary><AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}><DataBITable jobs={pendingServices} columnFilters={columnFilters} statusType="pending"/></AccordionDetails></Accordion>
                                <Accordion defaultExpanded={activeServices.length > 0} elevation={0} sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={activeServices.length === 0}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/tickcircle.svg" alt="Active" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Active ({activeServices.length})</Typography></Box></AccordionSummary><AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}><DataBITable jobs={activeServices} columnFilters={columnFilters} statusType="active"/></AccordionDetails></Accordion>
                                <Accordion defaultExpanded={rejectedServices.length > 0} elevation={0} sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }} disabled={rejectedServices.length === 0}><AccordionSummary expandIcon={<ExpandMoreIcon />}><Box sx={{ display: "flex", alignItems: "center" }}><img src="/assets/reject.svg" alt="Rejected" style={{ width: "20px", marginRight: "8px" }} /><Typography fontSize="16px" fontFamily="Gilroy-SemiBold">Rejected ({rejectedServices.length})</Typography></Box></AccordionSummary><AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}><DataBITable jobs={rejectedServices} columnFilters={columnFilters} statusType="rejected"/></AccordionDetails></Accordion>
                            </>
                        )}
                        {totalServicesOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor: "#FFFFFF", borderRadius: "8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No BI Tools Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No BI Tools match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}
                        {totalServicesOverall === 0 && !isLoading && (
                            <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No BI Tools" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No BI Tools have been added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first BI Tool.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default DataBI;



// // Filename: RAG.tsx (or RAGPage.tsx as you have it)
// import React from "react"; // Removed unused useState
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Button,
//     Container,
//     Typography,
//     Divider,
//     IconButton,
// } from "@mui/material";


// import { useAuth } from "../../../helpers/AuthProvider";
// import MainDrawer from "../../MainDrawer";
// import MiniBot from "../../MiniBot";

// // All dynamic data fetching, state management for RAGs, search, and column filters are removed
// // as RAGTable will now display static, hardcoded data and not accept data props.

// const DataBI: React.FC = () => {
//     const { user } = useAuth();
//     const navigate = useNavigate();

//     const canAddDataBI = true; // Example permission, adjust as needed

//     const handleAddDataBI = () => {
//         // Navigate to an add page or handle as a placeholder for a static view
//         navigate("/datasolutions/data-bi/add"); // Adjust this path if necessary
//         // For a truly static page without add functionality, you might comment out the onClick or the button.
//     };

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"datasolutions-data-bi"} /> {/* Ensure 'rag' is a valid index for your MainDrawer */}
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: "100vh",
//                 }}
//             >
//                 <MiniBot />
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px">
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 flexDirection: { xs: 'column', sm: 'row' },
//                                 justifyContent: "space-between",
//                                 alignItems: { xs: 'flex-start', sm: 'center' },
//                                 mx:2
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
//                                 Data Analytics & BI 
//                             </Typography>
//                             <Box sx={{display:'flex', justifyContent:'center'}}>
//                             {canAddDataBI && (
//                                 <Button
//                                     variant="contained"
//                                     sx={{
//                                         backgroundColor: "black", color: "white", padding: "8px 24px",
//                                         fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
//                                         borderRadius: "6px", '&:hover': { backgroundColor: '#333333' }
//                                     }}
//                                     onClick={handleAddDataBI}
//                                     // Adjust disabled logic based on DataWarehouses-specific permissions if different
//                                     disabled = {(user.roles.includes("cluster_owner") || user.roles.includes("viewer")) ? false : true}
//                                 >
//                                     Add New BI Tool
//                                 </Button>
//                             )}
//                             <IconButton
// 									  aria-label="Help" 
// 									  onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
// 									  sx={{
// 										padding: 0, 
// 										border: 'none',  
// 										ml:2,
// 										'&:hover': {
// 										  backgroundColor: 'action.hover', 
// 										},
										
// 									  }}
// 									>
// 									  <Box
// 										component="img"
// 										src="/assets/Help.svg" 
// 										alt="" 
// 										sx={{
// 										  width: 40,  
// 										  height: 40,
// 										  display: 'block', 
// 										}}
// 									  />
// 									</IconButton>
//                             </Box>
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                         {/* Search and Filter UI have been removed as the table is now static */}
//                     </Box>

//                     <Box m={{xs: 1, sm:2}}>
//                         {/* RAGTable component will render its own static data */}
//                         <Box
//                                                             display="flex" flexDirection="column" justifyContent="center" alignItems="center"
//                                                             sx={{
//                                                                 textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
//                                                                 backgroundColor: '#FFFFFF', borderRadius: "8px",
//                                                                 boxShadow: "0px 1px 3px rgba(0,0,0,0.05)",
//                                                                 height:"70vh"
//                                                             }}
//                                                         >
//                                                             <img src="/assets/inbox-icon.svg" alt="No AI Agents" style={{ width: '100px', marginBottom: '20px' }} />
//                                                             <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
//                                                                 No BI Tools added yet
//                                                             </Typography>
//                                                             <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
//                                                                 Click the button above to add your first BI Tool.
//                                                             </Typography>
//                                                         </Box>
                      
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default DataBI;


// import { Box, Typography } from "@mui/material";
// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import DashBoardPage from "../../DashBoardPage";
// import MainDrawer from "../../MainDrawer";
// import ComingSoonPage from "../../ComingSoonPage";
// const DataBI: React.FC = () => {
//     return(<Box sx={{ display: "flex"}}>
//     <MainDrawer index={"datasolutions-data-bi"} />
// {/* Helllo */}
//     <Box sx={{display:"flex" , alignItems:"center", justifyContent:'center' ,flexDirection: 'row', height:"100%", width:"100%"}}>
//  {/* <Typography fontFamily="Gilroy-Bold" fontSize="36px"  textAlign="center"> Coming Soon!!</Typography> */}
//  <ComingSoonPage/>
//     </Box>
//     </Box>)
// }
// export default DataBI;